import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Trash2, FileText, Image as ImageIcon, Palette, Volume2 } from 'lucide-react';
import { API, getHeaders } from './portalShared';

/**
 * Brand-supplied collateral — everything Freddy Media ops need to run
 * campaigns for the brand: logo, brand guidelines, primary colors,
 * voice/tone, plus a freeform file library for anything else (brand book
 * PDF, existing creative, sample decks, etc.).
 */
export default function BrandCollateral() {
  const [data, setData] = useState({ collateral: {}, files: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    axios.get(`${API}/portal/collateral`, { headers: getHeaders() })
      .then(r => setData({ collateral: r.data.collateral || {}, files: r.data.files || [] }))
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const patch = (key, value) => setData(d => ({ ...d, collateral: { ...d.collateral, [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portal/collateral`, data.collateral, { headers: getHeaders() });
      toast.success('Brand collateral saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/portal/collateral/files`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setData(d => ({ ...d, files: [...(d.files || []), r.data] }));
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/portal/collateral/files`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      // Auto-set the logo url on the structured collateral doc
      const nextCollateral = { ...data.collateral, logo_url: r.data.url };
      await axios.put(`${API}/portal/collateral`, nextCollateral, { headers: getHeaders() });
      setData(d => ({ ...d, collateral: nextCollateral, files: [...(d.files || []), r.data] }));
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeFile = async (fileId) => {
    try {
      await axios.delete(`${API}/portal/collateral/files/${fileId}`, { headers: getHeaders() });
      setData(d => ({ ...d, files: (d.files || []).filter(f => f.id !== fileId) }));
    } catch (e) { toast.error('Delete failed'); }
  };

  const c = data.collateral || {};

  return (
    <div className="bg-[#111827] border border-white/10 p-5 sm:p-6 space-y-5" data-testid="brand-collateral">
      <div>
        <h3 className="font-semibold text-white text-sm mb-1 flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#FF4343]" /> Brand Collateral
        </h3>
        <p className="text-xs text-[#9CA3AF]">Anything your Campaign Manager and creators should reference when producing content for you.</p>
      </div>

      {/* Logo */}
      <div>
        <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1.5">Logo</label>
        <div className="flex items-center gap-3">
          {c.logo_url ? (
            <img src={c.logo_url} alt="logo" className="w-16 h-16 object-contain bg-white/5 border border-white/10 p-1" data-testid="collateral-logo-preview" />
          ) : (
            <div className="w-16 h-16 bg-white/5 border border-dashed border-white/10 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-[#6B7280]" />
            </div>
          )}
          <label className="text-xs px-3 py-2 border border-white/20 text-white hover:bg-white/5 cursor-pointer flex items-center gap-1.5" data-testid="collateral-logo-upload">
            <Upload className="w-3.5 h-3.5" /> {c.logo_url ? 'Replace logo' : 'Upload logo'}
            <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={uploading} />
          </label>
        </div>
      </div>

      {/* Structured fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Primary color (hex)</label>
          <input value={c.primary_color || ''} onChange={e => patch('primary_color', e.target.value)}
            placeholder="#FF4343" data-testid="collateral-primary-color"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Secondary color (hex)</label>
          <input value={c.secondary_color || ''} onChange={e => patch('secondary_color', e.target.value)}
            placeholder="#090E33" data-testid="collateral-secondary-color"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Tagline / positioning statement</label>
          <input value={c.tagline || ''} onChange={e => patch('tagline', e.target.value)}
            placeholder="Where industry authority converts."
            data-testid="collateral-tagline"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1 flex items-center gap-1"><Volume2 className="w-3 h-3" /> Voice & tone</label>
          <textarea rows={3} value={c.voice_tone || ''} onChange={e => patch('voice_tone', e.target.value)}
            placeholder="e.g. Confident, direct, data-forward. Avoid jargon. Never use exclamation points."
            data-testid="collateral-voice-tone"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-[#10B981] block mb-1">Words to use</label>
          <textarea rows={3} value={c.do_say || ''} onChange={e => patch('do_say', e.target.value)}
            placeholder="Growth-stage · Buyer-led · ROI-driven"
            data-testid="collateral-do-say"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-[#FF4343] block mb-1">Words to avoid</label>
          <textarea rows={3} value={c.dont_say || ''} onChange={e => patch('dont_say', e.target.value)}
            placeholder="disrupt · cutting-edge · synergy"
            data-testid="collateral-dont-say"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Target audience notes</label>
          <textarea rows={2} value={c.audience_notes || ''} onChange={e => patch('audience_notes', e.target.value)}
            placeholder="e.g. Head of Sales at Series B–D SaaS companies (50–500 person orgs)"
            data-testid="collateral-audience-notes"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="collateral-save"
          className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50">
          {saving ? 'Saving…' : 'Save brand collateral'}
        </button>
      </div>

      {/* Freeform additional files */}
      <div className="border-t border-white/5 pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="text-xs font-semibold text-white flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#FF4343]" /> Additional files</label>
            <p className="text-[10px] text-[#6B7280] mt-0.5">Brand book, style guides, existing creative, sample decks. Anything your CM should reference.</p>
          </div>
          <label className="text-xs px-3 py-2 border border-white/20 text-white hover:bg-white/5 cursor-pointer flex items-center gap-1.5 flex-shrink-0" data-testid="collateral-file-upload">
            <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading…' : 'Add file'}
            <input type="file" className="hidden" onChange={uploadFile} disabled={uploading} />
          </label>
        </div>
        {(data.files || []).length === 0 ? (
          <p className="text-xs text-[#6B7280] italic">No files uploaded yet.</p>
        ) : (
          <div className="space-y-1.5">
            {data.files.map(f => (
              <div key={f.id} className="flex items-center gap-2 bg-white/5 px-3 py-2 text-xs" data-testid={`collateral-file-${f.id}`}>
                <FileText className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" />
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FF4343] flex-1 truncate">{f.name}</a>
                <span className="text-[10px] text-[#6B7280] font-mono">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => removeFile(f.id)} className="text-[#FF4343] hover:text-white" data-testid={`collateral-file-remove-${f.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
